import React from "react"
import type { SingularProductCard, MultipleProductCard } from "types"
import { siteUrl } from "utils/getSiteUrl"
import { useLocale } from "hooks"
import Script from "next/script"
import { removeTrailingSlash } from "utils/removeTrailingSlash"
import { defaultSchema } from "../DefaultSchema"

type Props = {
  products: Array<SingularProductCard | MultipleProductCard>
  url: string
  metaTitle: string
  metaDescription: string
  noIndex?: boolean
  noFollow?: boolean
  pageNumber?: number
}

export const PLPSeo: React.FC<Props> = ({ products, url, pageNumber }) => {
  const schemaUrl = "https://schema.org"
  const locale = useLocale()

  const urlSplit = url.split("/")
  if (urlSplit[1] === "us") {
    urlSplit.splice(1, 1)
  }

  const breadcrumbs = urlSplit.map((item, i) => {
    let itemName = "Beauty Pie Home"
    if (locale === "en-US") {
      itemName = "Beauty Pie Home US"
    }
    let itemId = `${siteUrl}${locale === "en-US" ? "/us" : ""}`
    if (i > 0) {
      for (let x = 1; x < i; x += 1) {
        if (urlSplit[x] !== "us") {
          itemId = `${itemId}/${urlSplit[x]}`
        }
      }
    }
    itemId = `${itemId}/${item}`
    return {
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@id": removeTrailingSlash(itemId),
        name: item || itemName,
      },
    }
  })

  if (pageNumber && pageNumber > 1) {
    const pageItem = {
      "@type": "ListItem",
      position: breadcrumbs.length + 1,
      item: {
        "@id": `${siteUrl}${url}?page=${pageNumber}`,
        name: `Page ${pageNumber}`,
      },
    }
    breadcrumbs.push(pageItem)
  }

  const breadCrumbsListSchema = {
    "@context": schemaUrl,
    "@type": "BreadcrumbList",
    itemListElement: [breadcrumbs],
  }

  const productList = products.map((product, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${siteUrl}${locale === "en-US" ? "/us" : ""}/products/${
      product?.variants[0].slug
    }`,
  }))

  const productListSchema = {
    "@context": schemaUrl,
    "@type": "ItemList",
    itemListElement: [productList],
  }

  const schema = {
    "@context": "http://schema.org",
    "@graph": [defaultSchema(locale), breadCrumbsListSchema, productListSchema],
  }

  return (
    <Script type="application/ld+json" id="ldjson">
      {JSON.stringify(schema)}
    </Script>
  )
}
